/**
 * The Ankiety tab's glyphs, inline. The pattern is `shared/ui/AuthIcons` and the geometry is the
 * canon's: a 16 unit grid, 1.5 stroke, round caps and joins, `fill: none`, `currentColor` so a
 * glyph follows its button's own ink through hover and refusal.
 *
 * ── Why they are here and not in `shared/` ───────────────────────────────────────────────────
 * The house rule promotes on the SECOND consumer. Every one of these is drawn by a survey surface
 * and nowhere else; `shared/ui/RecordIcons` and `EmptyStateIcons` already hold the glyphs two
 * unrelated surfaces share.
 *
 * ── `aria-hidden`, always ────────────────────────────────────────────────────────────────────
 * Every one of these stands inside a control that carries its own `aria-label` and `title`. A glyph
 * with a name of its own would be read twice.
 */

import styles from './SurveyIcons.module.css';

interface IconProps {
  readonly className?: string;
}

const BOX = {
  width: 16,
  height: 16,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/**
 * „Porównaj" — two panels with a double-headed arrow running between them.
 *
 * It was two bare rectangles, and at this size a trainer read them as two empty boxes rather than as
 * an act: nothing in the glyph said the two were being held against each other. The arrow is what
 * carries the verb, and it points BOTH ways because neither panel is the subject — the comparison is
 * symmetrical, which is also why the dialog has a „Zamień strony" at all.
 *
 * The panels are shortened to make room for it rather than the arrow being squeezed into the 3 unit
 * gap between them, where its heads would have collided with both edges at 1.5 stroke.
 */
export function CompareIcon({ className }: IconProps) {
  return (
    <svg {...BOX} className={className}>
      <rect x="2" y="1.5" width="4.5" height="7.75" rx="1" />
      <rect x="9.5" y="1.5" width="4.5" height="7.75" rx="1" />
      <path d="M3.25 13h9.5" />
      <path d="M5 11.25L3.25 13 5 14.75" />
      <path d="M11 11.25L12.75 13 11 14.75" />
    </svg>
  );
}

/** The tick — „Oznacz jako przejrzaną". */
export function ReviewedIcon({ className }: IconProps) {
  return (
    <svg {...BOX} className={className}>
      <path d="M3 8.5l3 3 7-7" />
    </svg>
  );
}

export function CloseIcon({ className }: IconProps) {
  return (
    <svg {...BOX} className={className}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

/**
 * The two arrows through a client's occurrences of one form. Recovered with the report dialog on
 * 17 September 2026 — they went with the modal on 16 September, and the pane that replaced it walks
 * the stream instead.
 */
export function PreviousIcon({ className }: IconProps) {
  return (
    <svg {...BOX} className={className}>
      <path d="M10 3L5 8l5 5" />
    </svg>
  );
}

export function NextIcon({ className }: IconProps) {
  return (
    <svg {...BOX} className={className}>
      <path d="M6 3l5 5-5 5" />
    </svg>
  );
}

/**
 * Two arrows passing — „Zamień strony".
 *
 * Each head is drawn as the full chevron rather than the single diagonal it used to be: one stroke
 * off the shaft reads as a fork at 14px, not as a point, and the two arrows then pointed at nothing
 * in particular.
 */
export function SwapIcon({ className }: IconProps) {
  return (
    <svg {...BOX} className={className}>
      <path d="M2.5 5.5h10M10 3l2.5 2.5L10 8" />
      <path d="M13.5 10.5h-10M6 8l-2.5 2.5L6 13" />
    </svg>
  );
}

/** A dash. The one mark for „nie odpowiedziano", drawn rather than written as an em dash so it
 * keeps its weight beside a tabular figure. */
export function BlankIcon({ className }: IconProps) {
  return (
    <svg {...BOX} className={className}>
      <path d="M4 8h8" />
    </svg>
  );
}

/** The play triangle over a clip's poster. */
export function PlayIcon({ className }: IconProps) {
  return (
    <svg {...BOX} className={className}>
      <path d="M6 4l6 4-6 4z" />
    </svg>
  );
}

/** A hollow ring — „zapisuję", the waiting twin of the tick above. See `SurveysTab`. */
export function WaitingIcon({ className }: IconProps) {
  return (
    <svg {...BOX} className={className}>
      <circle cx="8" cy="8" r="5" />
    </svg>
  );
}

/**
 * Which way a change went, as the canon draws it beside every signed figure: an arrow up for a
 * rise, down for a fall, and NOTHING for a change of zero — there is no direction to point in, and
 * a flat arrow would have to be invented.
 *
 * It is `aria-hidden` like every glyph here because the sign is already in the text beside it, and
 * it is the one glyph here that carries its own class — see `SurveyIcons.module.css`.
 */
export function ChangeArrowIcon({ sign }: { readonly sign: number }) {
  if (sign === 0) return null;
  return (
    <svg {...BOX} className={styles.arrow}>
      {sign > 0 ? <path d="M8 13V3M4 7l4-4 4 4" /> : <path d="M8 3v10M4 9l4 4 4-4" />}
    </svg>
  );
}
